import { asyncForEach, toGoLang, toGoLangInvoke } from "../common/windowFunctions";
import { logStatus } from "../utils";
const FsblSearchProviderNamePrefix = "FSBL.SearchProvider.";
export class SearchClientImpl {
    constructor(gluePromise, asyncConstructors, loggerClient) {
        this.gluePromise = gluePromise;
        this.asyncConstructors = asyncConstructors;
        this.loggerClient = loggerClient;
        this.providers = [];
        this.itemActionMap = {};
        const pw = asyncConstructors.addConstructor();
        this.gluePromise.then((g) => {
            this.glue = g;
            asyncConstructors.finishConstructor(pw);
        });
    }
    logStatus(method, type, data) {
        logStatus(this.loggerClient, this.glue, {
            api: "SearchClient",
            method, type, data
        });
    }
    async invokeProviderAction(provider) {
        console.warn("This method is deprecated");
    }
    async invokeItemAction(item, action) {
        this.logStatus("invokeItemAction", "start", { item, action });
        const actionHandlerName = `${item.provider}.ActionHandler`;
        const method = action.method ? action.method : actionHandlerName;
        const params = action.params ? action.params : {
            item,
            action,
            windowName: this.glue.windows.my()?.id ?? "Unknown Window"
        };
        const { err } = await toGoLangInvoke(() => this.glue.interop.invoke(method, params, action.target));
        if (err) {
            this.logStatus("invokeItemAction", "error", err);
        }
        this.logStatus("invokeItemAction", "return");
    }
    async search(params, cb) {
        this.logStatus("search", "start", params);
        const query = await this.glue.search.query({
            search: params.text
        });
        const results = [];
        query.onError(({ error }) => {
            this.logStatus("search", "error", error);
            cb?.(error);
        });
        query.onResults((q) => {
            results.push({
                data: q.results.map(({ id, displayName, type, metadata, description, iconURL, action, secondaryActions }) => {
                    const mainAction = action ? [action] : [];
                    const otherActions = secondaryActions ?? [];
                    const actions = mainAction.concat(otherActions);
                    return {
                        name: displayName ?? id,
                        type: type.name,
                        score: 1,
                        description,
                        icon: iconURL ? {
                            imageType: "url",
                            path: iconURL ?? ""
                        } : null,
                        category: type.displayName ?? type.name,
                        actions,
                        ...metadata,
                        provider: q.provider?.name
                    };
                }),
                provider: {
                    displayName: q.provider?.name.replace(FsblSearchProviderNamePrefix, "") ?? "Desktop",
                    channel: q.provider?.name ?? "Desktop",
                    providerActionCallback: false,
                    providerActionTitle: undefined
                },
            });
            this.logStatus("search", "return", { err: null, data: results });
            cb?.(null, results);
        });
    }
    async unregister(params, cb) {
        this.logStatus("unregister", "start", params);
        await this.asyncConstructors.ready();
        await asyncForEach(this.providers, async ({ name, provider }) => {
            if (name === params.name) {
                console.debug("FSBL.Clients.SearchClient.unregister removing", name);
                const actionHandlerName = `${FsblSearchProviderNamePrefix}${name}.ActionHandler`;
                const { err } = await toGoLang(() => this.glue.interop.unregister(actionHandlerName));
                if (err) {
                    console.warn("FSBL.Clients.SearchClient.unregister exception unregistering action handler", err);
                }
                await provider.unregister();
            }
        });
        this.providers = this.providers.filter(({ name }) => name !== params.name);
        this.logStatus("unregister", "return");
        cb?.();
    }
    async register(params, cb) {
        this.logStatus("register", "start", params);
        await this.asyncConstructors.ready();
        const name = `${FsblSearchProviderNamePrefix}${params.name}`;
        const actionHandlerName = `${name}.ActionHandler`;
        const myGlueProvider = await this.glue.search.registerProvider({ name });
        await toGoLang(() => this.glue.interop.register(actionHandlerName, async (params) => {
            console.debug("Action handler received", myGlueProvider.id, params);
            const func = this.itemActionMap[myGlueProvider.id];
            if (!func) {
                console.warn("Action provider not found for", myGlueProvider.id, params);
                return;
            }
            func(params);
        }));
        myGlueProvider.onQuery((query) => {
            params.searchCallback({ text: query.search }, (err, results) => {
                const realResults = Array.isArray(results) ? results : [results];
                if (err) {
                    query.error(err);
                    return;
                }
                if (realResults.length === 0) {
                    query.sendResult({
                        type: { name: "unknown" },
                    });
                }
                realResults.forEach((r) => {
                    const { name, type, description, actions = [], ...metadata } = r;
                    const iocdActions = actions.map((a) => {
                        return {
                            name: a.name,
                            method: actionHandlerName
                        };
                    });
                    query.sendResult({
                        type: { name: type },
                        description,
                        displayName: name,
                        action: iocdActions.shift(),
                        secondaryActions: iocdActions,
                        metadata: {
                            ...metadata
                        },
                    });
                });
                query.done();
            });
        });
        this.providers.push({
            name: params.name,
            provider: myGlueProvider,
        });
        if (params.itemActionCallback) {
            this.itemActionMap[myGlueProvider.id] = params.itemActionCallback;
        }
        if (params.providerActionTitle || params.providerActionCallback) {
            console.warn("Search Provider options 'providerActionTitle' and 'providerActionCallback' are deprecated.");
        }
        this.logStatus("register", "return", { err: null, status: "success" });
        cb?.(null, { status: "success" });
        return {
            err: null,
            data: { status: "success" }
        };
    }
}
